// The round state machine as a pure function (spec §4). The scheduled engine is a
// thin wrapper around this, so the OPEN → LOCKING → SETTLING → SETTLED transitions
// are deterministic and unit-testable.
//
// Note: opening a round (SCHEDULED → OPEN) is a separate, side-effecting step —
// it forms the groups — so this function never performs it; it leaves SCHEDULED
// untouched and only advances rounds that are already live.
/**
 * Pick the round the app should foreground as a league's `currentRoundId`.
 *
 * Preference order:
 *   1. the SOONEST still-pickable / in-play round (OPEN or LOCKING) — a dense
 *      tournament (e.g. the World Cup, chunked BY_DAY with a 48h open lead) can
 *      have several match-day rounds open at once, so we surface the nearest slate;
 *   2. else the MOST RECENT finished round (SETTLING/SETTLED), so once the last
 *      open round settles the pointer lands on it rather than getting stuck on an
 *      older already-settled round;
 *   3. else the soonest SCHEDULED round (nothing has opened yet).
 *
 * Pure and deterministic so the engine and its tests share one definition. The key
 * property: it always advances off a settled round while a later round is still
 * open — the old "only move when a brand-new round opens" rule could strand the
 * pointer on a settled round at the tail of a tournament.
 */
export function pickCurrentRoundId(rounds) {
    const live = rounds
        .filter((r) => r.status === 'OPEN' || r.status === 'LOCKING')
        .sort((a, b) => a.firstKickoffAt - b.firstKickoffAt);
    if (live.length)
        return live[0].roundId;
    const finished = rounds
        .filter((r) => r.status === 'SETTLING' || r.status === 'SETTLED')
        .sort((a, b) => b.firstKickoffAt - a.firstKickoffAt);
    if (finished.length)
        return finished[0].roundId;
    const scheduled = rounds
        .filter((r) => r.status === 'SCHEDULED')
        .sort((a, b) => a.firstKickoffAt - b.firstKickoffAt);
    return scheduled.length ? scheduled[0].roundId : null;
}
const isTerminal = (f) => f.status === 'SETTLED' || f.status === 'VOID';
const hasStarted = (f, now) => f.status !== 'SCHEDULED' || now >= f.kickoffAt;
/**
 * Has THIS fixture kicked off — so the pick on it is now frozen? A fixture locks the
 * moment it leaves SCHEDULED or its kickoff passes. This is the per-fixture unit of
 * locking: in FLEXIBLE rounds each pick freezes independently at its own fixture's
 * kickoff (spec §14.1), so the board/editor grade editability one fixture at a time.
 * (EXACT rounds freeze the whole card together at the round's first kickoff instead.)
 */
export function fixtureLocked(fixture, now) {
    return hasStarted(fixture, now);
}
/**
 * The status a live round should be in given the clock and its fixtures:
 *   every fixture terminal            → SETTLED   (caller runs settlement)
 *   every fixture kicked off          → SETTLING
 *   at least the first kicked off     → LOCKING
 *   otherwise                         → OPEN
 * SCHEDULED and SETTLED are sticky (opening and settlement are explicit steps).
 */
export function nextRoundStatus(current, fixtures, now, firstKickoffAt) {
    if (current === 'SETTLED')
        return 'SETTLED';
    if (current === 'SCHEDULED')
        return 'SCHEDULED';
    if (fixtures.length === 0)
        return current;
    if (fixtures.every(isTerminal))
        return 'SETTLED';
    if (fixtures.every((f) => hasStarted(f, now)))
        return 'SETTLING';
    if (now >= firstKickoffAt)
        return 'LOCKING';
    return 'OPEN';
}
/**
 * The instant a player's entry is FULLY locked for a round, by pick mode (spec §14.1).
 *
 *   EXACT (standard weekly leagues) — a single SHARED deadline: the round's first
 *     kickoff. The whole slate locks at once, so everyone commits on equal
 *     information (FPL-style) and no one gains an edge — or pays a penalty — for
 *     which kickoff slot they happened to back.
 *   FLEXIBLE (day-chunked tournaments, e.g. the World Cup) — PER-FIXTURE: every pick
 *     freezes independently at its OWN fixture's kickoff (see `fixtureLocked`), so a
 *     player keeps building their card across the match-day — backing the games they
 *     haven't missed — and only loses the ability to touch a pick once THAT match
 *     starts. The card as a whole is therefore "fully locked" only once the LAST of
 *     the player's picks has kicked off, which is what this returns: `max(picked)`.
 *
 * `pickedKickoffs` are the kickoff times (epoch ms) of the fixtures the player has
 * already backed; they only affect FLEXIBLE. An empty list yields the round's
 * deadline for EXACT and +Infinity for FLEXIBLE (a fresh entry can still form
 * mid-slate). A missing `pickMode` is treated as EXACT. NB: in FLEXIBLE this is the
 * whole-card boundary; individual picks lock earlier, per `fixtureLocked`.
 */
export function entryLockAt(round, pickedKickoffs = []) {
    if (round.pickMode === 'FLEXIBLE') {
        return pickedKickoffs.length ? Math.max(...pickedKickoffs) : Number.POSITIVE_INFINITY;
    }
    return round.firstKickoffAt;
}
/**
 * Is ANY part of the player's entry still editable right now? False once the round is
 * no longer taking entries (any status other than OPEN/LOCKING). Used identically by
 * the web app (pre-submit UX) and the authoritative `submitEntry` callable.
 *
 * In FLEXIBLE this is a card-level gate ("can I do anything?"). It stays true for the
 * whole time the round is OPEN/LOCKING, because while a round is taking entries there
 * is ALWAYS at least one upcoming fixture to build a fresh card on — the round only
 * flips to SETTLING once EVERY fixture has kicked off (`nextRoundStatus`). It must NOT
 * collapse to `max(pickedKickoffs)`: a player who backed only an early match would
 * otherwise lose access to every still-open fixture the instant that one game starts
 * (the real-world bug that motivated this). *Which* individual picks are frozen is a
 * separate, per-fixture decision made by `fixtureLocked` / `flexibleFreezeViolation`,
 * so an already-started card stays locked even while the entry as a whole is editable.
 *
 * In EXACT the whole card is the unit and locks together at the round's first kickoff,
 * so the lock instant (`entryLockAt`, which ignores `pickedKickoffs` for EXACT) is the
 * only check the UI needs. `pickedKickoffs` is therefore unused for editability and
 * kept only for call-site symmetry; `entryLockAt` still uses it for the "fully locked"
 * display/settlement boundary.
 */
export function isEntryEditable(round, pickedKickoffs, now) {
    if (round.status !== 'OPEN' && round.status !== 'LOCKING')
        return false;
    if (round.pickMode === 'FLEXIBLE')
        return true;
    return now < entryLockAt(round, pickedKickoffs);
}
//# sourceMappingURL=lifecycle.js.map